import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTripVersion } from '../hooks/useDriveDetection';
import OnboardingWizard from '../components/OnboardingWizard';
import ActiveTripBanner from '../components/ActiveTripBanner';
import DriveDetector from '../plugins/DriveDetector';

const IRS_RATE = 0.70;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

function encodePolyline(coords) {
  let encoded = '';
  let prevLat = 0, prevLng = 0;
  for (const { lat, lng } of coords) {
    let dLat = Math.round((lat - prevLat) * 1e5);
    let dLng = Math.round((lng - prevLng) * 1e5);
    prevLat = lat; prevLng = lng;
    for (const d of [dLat, dLng]) {
      let v = d < 0 ? ~(d << 1) : d << 1;
      while (v >= 0x20) { encoded += String.fromCharCode((0x20 | (v & 0x1f)) + 63); v >>= 5; }
      encoded += String.fromCharCode(v + 63);
    }
  }
  return encoded;
}

function sampleCoords(coords, maxPoints = 30) {
  if (!coords || coords.length <= maxPoints) return coords || [];
  const step = (coords.length - 1) / (maxPoints - 1);
  const sampled = [];
  for (let i = 0; i < maxPoints; i++) sampled.push(coords[Math.round(i * step)]);
  return sampled;
}

function getStaticMapUrl(trip, width = 400, height = 160) {
  if (!MAPS_KEY) return null;
  const hasCoords = trip.coordinates?.length > 1;
  const hasEndpoints = trip.startLat && trip.endLat;
  if (!hasCoords && !hasEndpoints) return null;

  const base = 'https://maps.googleapis.com/maps/api/staticmap';
  const params = new URLSearchParams({
    size: `${width}x${height}`,
    scale: '2',
    maptype: 'roadmap',
    style: 'feature:all|element:geometry|color:0x1a1a2e',
    key: MAPS_KEY,
  });
  // Dark map styles
  params.append('style', 'feature:all|element:labels.text.fill|color:0x8a8a8a');
  params.append('style', 'feature:all|element:labels.text.stroke|color:0x1a1a2e');
  params.append('style', 'feature:road|element:geometry|color:0x2d2d44');
  params.append('style', 'feature:road.highway|element:geometry|color:0x3d3d5c');
  params.append('style', 'feature:water|element:geometry|color:0x0e0e1a');
  params.append('style', 'feature:poi|visibility:off');
  params.append('style', 'feature:transit|visibility:off');

  if (hasCoords) {
    const sampled = sampleCoords(trip.coordinates);
    const poly = encodePolyline(sampled);
    params.append('path', `color:0x14B8A6ff|weight:3|enc:${poly}`);
    const first = trip.coordinates[0];
    const last = trip.coordinates[trip.coordinates.length - 1];
    params.append('markers', `color:green|size:small|${first.lat},${first.lng}`);
    params.append('markers', `color:red|size:small|${last.lat},${last.lng}`);
  } else if (hasEndpoints) {
    params.append('markers', `color:green|size:small|${trip.startLat},${trip.startLng}`);
    params.append('markers', `color:red|size:small|${trip.endLat},${trip.endLng}`);
    params.append('path', `color:0x14B8A6ff|weight:3|${trip.startLat},${trip.startLng}|${trip.endLat},${trip.endLng}`);
  }

  return `${base}?${params.toString()}`;
}

export default function TripList() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const tripVersion = useTripVersion();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [activeTab, setActiveTab] = useState('weekly');

  useEffect(() => {
    async function fetchData() {
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          const hasVehicles = data.vehicles && Object.keys(data.vehicles).length > 0;
          const onboardingDone = data.profile?.onboardingComplete;
          const isNativePlatform = Capacitor.isNativePlatform();
          const nativeOnboardingDone = data.profile?.nativeOnboardingComplete;

          // On native, also check if location is actually granted —
          // flag may be true from a previous broken attempt
          let locationGranted = true;
          if (isNativePlatform) {
            try {
              const perms = await DriveDetector.checkPermissions();
              locationGranted = perms.location === 'always' || perms.location === 'whenInUse';
            } catch (e) {
              locationGranted = false;
            }
          }

          if (!hasVehicles || !onboardingDone || (isNativePlatform && (!nativeOnboardingDone || !locationGranted))) {
            setShowOnboarding(true);
          }
          setSubscription(data.subscription || {});
        }

        const tripsRef = collection(db, 'users', currentUser.uid, 'trips');
        const q = query(tripsRef, orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        const tripData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTrips(tripData);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, [currentUser.uid, tripVersion]);

  function getFilteredTrips() {
    const now = new Date();
    return trips.filter((t) => {
      const tripDate = new Date(t.date);
      if (activeTab === 'daily') {
        return tripDate.toDateString() === now.toDateString();
      }
      if (activeTab === 'weekly') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return tripDate >= weekAgo;
      }
      if (activeTab === 'monthly') {
        return tripDate.getMonth() === now.getMonth() && tripDate.getFullYear() === now.getFullYear();
      }
      return true;
    });
  }

  const filtered = getFilteredTrips();
  const totalMiles = filtered.reduce((sum, t) => sum + (t.distance || 0), 0);
  const totalDeduction = filtered.reduce((sum, t) => sum + (t.deduction || t.distance * IRS_RATE || 0), 0);
  const currentYearTrips = trips.filter((t) => new Date(t.date).getFullYear() === new Date().getFullYear());
  const yearMiles = currentYearTrips.reduce((sum, t) => sum + (t.distance || 0), 0);
  const yearDeduction = currentYearTrips.reduce((sum, t) => sum + (t.deduction || t.distance * IRS_RATE || 0), 0);

  if (showOnboarding) {
    return <OnboardingWizard onComplete={() => setShowOnboarding(false)} />;
  }

  const isSubscribed = subscription?.status === 'active' || subscription?.status === 'trialing';

  if (subscription && !isSubscribed) {
    return (
      <div className="screen-container">
        <h2>Drives</h2>
        <div className="pro-gate">
          <div className="pro-gate-icon">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="#0D9488">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
          </div>
          <h3>Subscribe to MileDeduct</h3>
          <p>Start your free trial to unlock mileage tracking, drive detection, and all features.</p>
          <button className="btn btn-primary" onClick={() => navigate('/subscription')}>
            Start Free Trial
          </button>
        </div>
      </div>
    );
  }

  async function refreshTrips() {
    const tripsRef = collection(db, 'users', currentUser.uid, 'trips');
    const q = query(tripsRef, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    setTrips(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  }

  return (
    <div className="screen-container">
      {Capacitor.isNativePlatform() && <ActiveTripBanner onTripEnded={refreshTrips} />}

      {/* Period Tabs */}
      <div className="period-tabs">
        {['daily', 'weekly', 'monthly'].map((tab) => (
          <button
            key={tab}
            className={`period-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Add Trip Button (web only) */}
      {!Capacitor.isNativePlatform() && (
        <div className="web-add-trip">
          <button className="btn btn-primary" onClick={() => navigate('/trip/new/manual')}>
            + Log a Trip
          </button>
        </div>
      )}

      {/* Year Summary */}
      <div className="year-summary">
        <div className="year-summary-item">
          <span className="year-summary-value">{yearMiles.toFixed(1)} mi</span>
          <span className="year-summary-label">{new Date().getFullYear()} Work Miles</span>
        </div>
        <div className="year-summary-item">
          <span className="year-summary-value year-deduction">${yearDeduction.toFixed(2)}</span>
          <span className="year-summary-label">Tax Deductions</span>
        </div>
      </div>

      {/* Period Stats */}
      <div className="trip-summary">
        <div className="summary-card">
          <span className="summary-value">{totalMiles.toFixed(1)}</span>
          <span className="summary-label">Miles</span>
        </div>
        <div className="summary-card">
          <span className="summary-value" style={{ color: '#34D399' }}>${totalDeduction.toFixed(2)}</span>
          <span className="summary-label">Deductions</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{filtered.length}</span>
          <span className="summary-label">Trips</span>
        </div>
      </div>

      {/* Trip List */}
      {loading ? (
        <div className="loading">Loading trips...</div>
      ) : trips.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg viewBox="0 0 120 80" width="120" height="80">
              <path d="M 15 60 Q 40 20 60 40 Q 80 60 105 30" fill="none" stroke="#E2E8F0" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="105" cy="30" r="6" fill="#0D9488" opacity="0.2" />
              <circle cx="105" cy="30" r="3" fill="#0D9488" />
            </svg>
          </div>
          <h3>You're all set!</h3>
          <p>We'll automatically track your trips. Tap the + button to add your first trip.</p>
        </div>
      ) : (
        <div className="trip-list">
          {filtered.map((trip) => {
            const mapUrl = getStaticMapUrl(trip);
            const deduction = trip.deduction || trip.distance * IRS_RATE;
            return (
              <div key={trip.id} className="trip-card-v2" onClick={() => navigate(`/trip/${trip.id}`)}>
                {mapUrl ? (
                  <div className="trip-card-map">
                    <img src={mapUrl} alt="Route" loading="lazy" />
                    <div className="trip-card-map-overlay">
                      <span className="trip-card-distance">{trip.distance?.toFixed(1)} mi</span>
                    </div>
                  </div>
                ) : (
                  <div className="trip-card-map trip-card-map-placeholder">
                    <svg viewBox="0 0 400 100" width="100%" height="100%">
                      <path d="M 30 70 Q 120 20 200 50 Q 280 80 370 30" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="30" cy="70" r="4" fill="#14B8A6" />
                      <circle cx="370" cy="30" r="4" fill="#EF4444" />
                    </svg>
                    <div className="trip-card-map-overlay">
                      <span className="trip-card-distance">{trip.distance?.toFixed(1)} mi</span>
                    </div>
                  </div>
                )}
                <div className="trip-card-info">
                  <div className="trip-card-row-top">
                    <div className="trip-card-route">
                      <div className="trip-card-endpoint">
                        <span className="trip-card-dot start" />
                        <span className="trip-card-addr">{trip.startAddress || 'Start location'}</span>
                      </div>
                      <div className="trip-card-endpoint">
                        <span className="trip-card-dot end" />
                        <span className="trip-card-addr">{trip.endAddress || 'End location'}</span>
                      </div>
                    </div>
                    <span className="trip-card-deduction">${deduction?.toFixed(2)}</span>
                  </div>
                  <div className="trip-card-row-bottom">
                    <span className="trip-card-date">
                      {new Date(trip.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className={`trip-card-purpose-badge ${(trip.purpose || 'Business').toLowerCase()}`}>
                      {trip.purpose || 'Business'}
                    </span>
                    {trip.durationMinutes && (
                      <span className="trip-card-duration">
                        {trip.durationMinutes >= 60
                          ? `${Math.floor(trip.durationMinutes / 60)}h ${Math.round(trip.durationMinutes % 60)}m`
                          : `${Math.round(trip.durationMinutes)}m`}
                      </span>
                    )}
                    {trip.trackingMethod && (
                      <span className="trip-card-method">
                        {trip.trackingMethod === 'manual-gps' ? 'GPS' : trip.trackingMethod === 'auto-gps' ? 'Auto' : 'Manual'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
